'use client'

import Image from 'next/image'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'

import { Menu, X } from 'lucide-react'

import { cn } from '../../lib/utils'
import { DataChip } from '../kinetic/DataChip'
import { Container } from '../layout/container'
import { Button } from '../ui/button'
import { AuthProvider } from './auth-provider'

const navigation = [
  { label: 'Home', href: '' },
  { label: 'Services', href: 'services' },
  { label: 'Case Studies', href: 'case-studies' },
  { label: 'Insights', href: 'post' },
  { label: 'Workshops', href: 'workshops' },
  { label: 'About', href: 'about-us' },
  { label: 'Contact', href: 'contact-us' },
]

export default function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const pathname = usePathname()
  const router = useRouter()

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20)
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const isActive = (href: string) => {
    if (href === '' && (pathname === '/' || pathname === '')) return true
    if (href !== '' && pathname.startsWith(`/${href}`)) return true
    return false
  }

  return (
    <header
      className={cn(
        'fixed left-0 right-0 top-0 z-50 transition-all duration-300',
        isScrolled
          ? 'bg-kinetic-surface/75 border-kinetic-primary-container/15 border-b backdrop-blur-md'
          : 'bg-kinetic-surface/55 backdrop-blur-sm'
      )}
    >
      <Container size="custom" width="large" className="relative z-10 py-0">
        <div className="flex items-center justify-between py-6">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link href="/" className="group flex items-center">
              <div className="relative flex">
                <Image
                  src="/Cloud_Shuttle_Logo_Black_v1.png"
                  width={100}
                  height={50}
                  alt="Cloud Shuttle Logo"
                  className={cn(
                    'h-12 w-auto transition-transform duration-300 group-hover:scale-105',
                    'invert'
                  )}
                />
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav id="main-navigation" className="mx-12 hidden flex-1 justify-center lg:flex">
            <ul className="flex space-x-2">
              {navigation.map(item => (
                <li key={item.href} className="relative">
                  <Link
                    href={item.href === '' ? '/' : `/${item.href}`}
                    className={cn(
                      'block px-3 py-2 font-headline text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-150',
                      isActive(item.href)
                        ? 'border-b-2 border-kinetic-primary-container text-kinetic-primary-container'
                        : 'text-kinetic-on-surface-variant hover:text-kinetic-on-surface'
                    )}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-3 pr-4 md:pr-6">
            <AuthProvider />

            <div className="hidden lg:block">
              <DataChip label="SYS_READY" />
            </div>

            {/* CTA Button */}
            <Button
              variant="kinetic"
              size="lg"
              className={cn('hidden px-6 py-2.5 text-xs md:flex')}
              onClick={() => router.push('/contact-us')}
            >
              Start Session
            </Button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={cn(
                'ml-2 rounded-lg p-2 transition-colors lg:hidden',
                isMobileMenuOpen
                  ? 'bg-kinetic-primary-container text-kinetic-on-primary'
                  : 'hover:bg-kinetic-surface-container/40 text-kinetic-on-surface'
              )}
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            className={cn(
              'absolute left-0 right-0 top-full shadow-lg backdrop-blur-md lg:hidden',
              'bg-kinetic-surface/92 border-kinetic-primary-container/15 border-t'
            )}
          >
            <nav className="px-4 py-3">
              <ul className="space-y-2">
                {navigation.map(item => (
                  <li key={item.href}>
                    <Link
                      href={item.href === '' ? '/' : `/${item.href}`}
                      className={cn(
                        'flex items-center px-4 py-3 font-headline text-sm font-semibold uppercase tracking-[0.12em] transition-colors',
                        isActive(item.href)
                          ? 'bg-kinetic-surface-container-highest/40 text-kinetic-primary-container'
                          : 'hover:bg-kinetic-surface-container/40 text-kinetic-on-surface-variant hover:text-kinetic-on-surface'
                      )}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
                {/* Mobile CTA */}
                <li className="pt-2">
                  <Button
                    variant="kinetic"
                    size="lg"
                    className={cn('w-full px-6 py-2.5 text-xs')}
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      router.push('/contact-us')
                    }}
                  >
                    Start Session
                  </Button>
                </li>
              </ul>
            </nav>
          </div>
        )}
      </Container>
    </header>
  )
}
